import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/api';

// Helper function to extract error message from error response
const extractErrorMessage = (error, defaultMessage) => {
  let errorMessage = defaultMessage;
  
  if (error.response?.data) {
    const errorData = error.response.data;
    if (typeof errorData === 'object') {
      errorMessage = errorData.detail ||
                     errorData.message ||
                     (errorData.messages && JSON.stringify(errorData.messages)) ||
                     JSON.stringify(errorData);
    } else {
      errorMessage = String(errorData);
    }
  } else if (error.message) {
    errorMessage = error.message;
  }
  
  return errorMessage;
};

// Get user from localStorage
const user = JSON.parse(localStorage.getItem('user'));
const token = localStorage.getItem('token');

// Debug logging for initialization
console.log('AuthSlice Initialization Debug:', {
  localStorageUser: localStorage.getItem('user'),
  localStorageToken: localStorage.getItem('token'),
  parsedUser: user,
  parsedToken: token,
  isAuthenticatedInitial: !!token
});

// Initial state - simplified to prevent authentication loops
const initialState = {
  user: user || null,
  token: token || null,
  permissions: user?.user_permissions || [],
  groups: user?.groups || [],
  isTenantAdmin: user?.is_tenant_admin || false,
  // Feature licensing: null = every module licensed; else the licensed RBAC
  // codename prefixes (docs/claude/licensing.md).
  licensedPrefixes: user?.licensed_prefixes ?? null,
  isAuthenticated: !!token, // Simple check: if we have a token, consider authenticated
  isLoading: false, // Don't start in loading state to prevent loops
  error: null,
  passwordResetStatus: null,
  // MFA challenge state (set when /auth/token/ returns mfa_required instead of JWTs)
  mfaRequired: false,
  mfaSetupRequired: false,
  mfaToken: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);

      // Conditional MFA: the backend withholds JWTs and asks for a second factor.
      if (response.data.mfa_required) {
        return {
          mfaRequired: true,
          mfaToken: response.data.mfa_token,
          setupRequired: !!response.data.setup_required,
        };
      }

      // Store the access token. The refresh token is only kept in localStorage in
      // legacy mode; when AUTH_REFRESH_COOKIE_ENABLED the backend omits `refresh`
      // from the body and sets an HttpOnly cookie instead (SECURITY 2.1).
      localStorage.setItem('token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      } else {
        localStorage.removeItem('refreshToken');
      }

      // Get user info
      const userResponse = await authService.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userResponse.data));

      return {
        user: userResponse.data,
        token: response.data.access,
      };
    } catch (error) {
      // Log the error from login or getCurrentUser
      console.error('authSlice: login thunk error:', error);
      // Ensure we extract the message correctly from the error object.
      // The error object might be from axios (with error.response) or a generic error.
      let errorMessage = 'Login failed';
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        } else {
          errorMessage = String(errorData);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// MFA: verify the second-factor code (or recovery code) at login and complete sign-in
export const mfaVerify = createAsyncThunk(
  'auth/mfaVerify',
  async ({ mfaToken, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.mfaVerify(mfaToken, otp);
      localStorage.setItem('token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      } else {
        localStorage.removeItem('refreshToken');
      }
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return { user: response.data.user, token: response.data.access };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Verification failed'));
    }
  }
);

// MFA: start setup — returns { config_url, qr_png_base64, secret }. mfaToken is
// passed during first-time login; omitted when enabling from the security page.
export const mfaSetupBegin = createAsyncThunk(
  'auth/mfaSetupBegin',
  async ({ mfaToken } = {}, { rejectWithValue }) => {
    try {
      const response = await authService.mfaSetupBegin(mfaToken);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Could not start MFA setup'));
    }
  }
);

// MFA: confirm the first code. During login this returns JWTs and completes
// sign-in; from the security page it just enables MFA. Always returns recovery codes.
export const mfaSetupConfirm = createAsyncThunk(
  'auth/mfaSetupConfirm',
  async ({ otp, mfaToken } = {}, { rejectWithValue }) => {
    try {
      const response = await authService.mfaSetupConfirm(otp, mfaToken);
      const data = response.data;
      // Login-time setup returns tokens — persist the session.
      if (data.access) {
        localStorage.setItem('token', data.access);
        if (data.refresh) {
          localStorage.setItem('refreshToken', data.refresh);
        } else {
          localStorage.removeItem('refreshToken');
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        return {
          loggedIn: true,
          user: data.user,
          token: data.access,
          recoveryCodes: data.recovery_codes || [],
        };
      }
      return { loggedIn: false, recoveryCodes: data.recovery_codes || [] };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Could not enable MFA'));
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  // Remove user and tokens from localStorage
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  return null;
});

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if we're already refreshing to prevent infinite loops
      const state = getState();
      if (state.auth.isLoading) {
        console.log('refreshToken: Already loading, skipping duplicate request');
        return rejectWithValue('Refresh already in progress');
      }
      
      // Check if we recently attempted a refresh (within last 5 seconds)
      const lastRefreshAttempt = localStorage.getItem('lastRefreshAttempt');
      const now = Date.now();
      if (lastRefreshAttempt && (now - parseInt(lastRefreshAttempt)) < 5000) {
        console.log('refreshToken: Too soon since last refresh attempt, skipping');
        return rejectWithValue('Too soon since last refresh');
      }
      
      // Record this refresh attempt
      localStorage.setItem('lastRefreshAttempt', now.toString());

      // Legacy mode: refresh token lives in localStorage. Cookie mode (SECURITY 2.1):
      // it's an HttpOnly cookie sent automatically, so a missing localStorage value
      // is NOT fatal — let the backend read the cookie.
      const storedRefresh = localStorage.getItem('refreshToken');

      console.log('refreshToken: Attempting to refresh token...');
      const response = await authService.refreshToken(storedRefresh);
      localStorage.setItem('token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      }
      
      // Also get user info if we don't have it
      const userResponse = await authService.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      console.log('refreshToken: Token refresh successful');
      return {
        token: response.data.access,
        user: userResponse.data,
      };
    } catch (error) {
      // If refresh token fails, log the user out
      console.error('Token refresh failed:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('lastRefreshAttempt');
      
      return rejectWithValue(extractErrorMessage(error, 'Session expired'));
    }
  }
);

// Refresh permissions for already-authenticated users (e.g. after role assignment)
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const userResponse = await authService.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      return userResponse.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to fetch user'));
    }
  }
);

// Force change password (first login)
export const forceChangePassword = createAsyncThunk(
  'auth/forceChangePassword',
  async ({ currentPassword, newPassword }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authService.forceChangePassword(currentPassword, newPassword);
      // Refresh user data so must_change_password flag is updated
      const userResponse = await authService.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      return { user: userResponse.data, detail: response.data.detail };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to change password'));
    }
  }
);

// Password reset thunks
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to send password reset email'));
    }
  }
);

export const validateResetToken = createAsyncThunk(
  'auth/validateResetToken',
  async ({ token, uid }, { rejectWithValue }) => {
    try {
      // Log the parameters being sent
      console.log('Validating reset token with:', { token, uid });
      const response = await authService.validateResetToken(token, uid);
      return response.data;
    } catch (error) {
      console.error('Token validation error:', error);
      return rejectWithValue(extractErrorMessage(error, 'Invalid or expired token'));
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, uid, password }, { rejectWithValue }) => {
    try {
      // Log the parameters being sent
      console.log('Resetting password with:', { token, uid, password: '***' });
      const response = await authService.resetPassword(token, uid, password);
      return response.data;
    } catch (error) {
      console.error('Password reset error:', error);
      return rejectWithValue(extractErrorMessage(error, 'Failed to reset password'));
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPasswordResetStatus: (state) => {
      state.passwordResetStatus = null;
    },
    clearMfaState: (state) => {
      state.mfaRequired = false;
      state.mfaSetupRequired = false;
      state.mfaToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        // MFA challenge required — hold here until the second factor is provided.
        if (action.payload.mfaRequired) {
          state.isAuthenticated = false;
          state.mfaRequired = true;
          state.mfaSetupRequired = !!action.payload.setupRequired;
          state.mfaToken = action.payload.mfaToken;
          state.error = null;
          return;
        }
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.permissions = action.payload.user?.user_permissions || [];
        state.groups = action.payload.user?.groups || [];
        state.isTenantAdmin = action.payload.user?.is_tenant_admin || false;
        state.licensedPrefixes = action.payload.user?.licensed_prefixes ?? null;
        state.error = null;
        state.mfaRequired = false;
        state.mfaSetupRequired = false;
        state.mfaToken = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.groups = [];
        state.isTenantAdmin = false;
        state.licensedPrefixes = null;
        // Fixed: more reliable error message handling
        state.error = action.payload || 'Login failed. Please try again.';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.groups = [];
        state.isTenantAdmin = false;
        state.licensedPrefixes = null;
        state.error = null; // Added: clear error on logout
      })
      // Refresh token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.permissions = action.payload.user?.user_permissions || [];
        state.groups = action.payload.user?.groups || [];
        state.isTenantAdmin = action.payload.user?.is_tenant_admin || false;
        state.licensedPrefixes = action.payload.user?.licensed_prefixes ?? null;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.groups = [];
        state.isTenantAdmin = false;
        state.licensedPrefixes = null;
        state.error = 'Session expired. Please log in again.'; // Added: informative error message
      })
      
      // Fetch current user (background permission refresh)
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.permissions = action.payload?.user_permissions || [];
        state.groups = action.payload?.groups || [];
        state.isTenantAdmin = action.payload?.is_tenant_admin || false;
        state.licensedPrefixes = action.payload?.licensed_prefixes ?? null;
      })

      // MFA verify (login challenge) — completes sign-in
      .addCase(mfaVerify.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mfaVerify.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.permissions = action.payload.user?.user_permissions || [];
        state.groups = action.payload.user?.groups || [];
        state.isTenantAdmin = action.payload.user?.is_tenant_admin || false;
        state.licensedPrefixes = action.payload.user?.licensed_prefixes ?? null;
        state.error = null;
        state.mfaRequired = false;
        state.mfaSetupRequired = false;
        state.mfaToken = null;
      })
      .addCase(mfaVerify.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Verification failed';
      })

      // MFA setup confirm — may also complete sign-in (login-time setup)
      .addCase(mfaSetupConfirm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mfaSetupConfirm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload.loggedIn) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.permissions = action.payload.user?.user_permissions || [];
          state.groups = action.payload.user?.groups || [];
          state.isTenantAdmin = action.payload.user?.is_tenant_admin || false;
          state.licensedPrefixes = action.payload.user?.licensed_prefixes ?? null;
          state.mfaRequired = false;
          state.mfaSetupRequired = false;
          state.mfaToken = null;
        }
      })
      .addCase(mfaSetupConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Could not enable MFA';
      })

      // Force Change Password
      .addCase(forceChangePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forceChangePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(forceChangePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to change password';
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetStatus = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.passwordResetStatus = 'email-sent';
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to send password reset email';
      })
      
      // Validate Reset Token
      .addCase(validateResetToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateResetToken.fulfilled, (state) => {
        state.isLoading = false;
        state.passwordResetStatus = 'token-valid';
      })
      .addCase(validateResetToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Invalid or expired token';
        state.passwordResetStatus = 'token-invalid';
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.passwordResetStatus = 'reset-success';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to reset password';
      });
  },
});

export const { clearError, clearPasswordResetStatus, clearMfaState } = authSlice.actions;
export default authSlice.reducer;